import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toolHelp, type ToolHelpId } from '../help/toolHelp';
import './HelpTooltip.css';

interface Props {
  id: ToolHelpId;
}

interface TipPos {
  left: number;
  top: number;
  placement: 'top' | 'bottom';
}

// A small "?" icon that reveals a tool's explanation on hover AND keyboard
// focus (for accessibility). The tip is rendered in a portal on document.body
// with fixed positioning so it can never be clipped by scrolling panels.
export function HelpTooltip({ id }: Props) {
  const help = toolHelp[id];
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<TipPos>({
    left: 0,
    top: 0,
    placement: 'top',
  });
  const tipId = useId();

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const btn = btnRef.current;
      const tip = tipRef.current;
      if (!btn) return;
      const b = btn.getBoundingClientRect();
      const tipH = tip?.offsetHeight ?? 120;
      const tipW = tip?.offsetWidth ?? 240;
      const margin = 8;

      let placement: 'top' | 'bottom' = 'top';
      let top = b.top - tipH - margin;
      if (top < margin) {
        placement = 'bottom';
        top = b.bottom + margin;
      }
      let left = b.left + b.width / 2 - tipW / 2;
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - tipW - margin),
      );
      setPos({ left, top, placement });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return (
    <span className="help">
      <button
        ref={btnRef}
        type="button"
        className="help__btn"
        aria-label={`Help: ${help.label}`}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        ?
      </button>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            id={tipId}
            className={`help__tip help__tip--${pos.placement}`}
            style={{ left: pos.left, top: pos.top }}
          >
            <span className="help__title">{help.label}</span>
            <span className="help__what">{help.what}</span>
            <span className="help__how">
              <strong>How:</strong> {help.how}
            </span>
          </div>,
          document.body,
        )}
    </span>
  );
}
